import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  TitleStrategy,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@awo-care/shared-auth';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { CustomerErrorHandlerService } from './core/services/customer-error-handler.service';
import { UserService } from './core/services/user.service';
import { PageTitleStrategy } from './core/services/page-title.strategy';
import { provideHttpClient, withXhr } from '@angular/common/http';
import {
  GOOGLE_MAPS_API_KEY,
  PHONE_INPUT_FLAG_BASE_URL,
} from '@solidgroup-agency/ui-dynamic-form';
import {
  MatDateFormats,
  MAT_DATE_FORMATS,
  DateAdapter,
} from '@angular/material/core';
import {
  CustomGermanDateAdapter,
  provideCrossDomainCookieConsent,
} from '@awo-care/utils';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideFirebase,
  USE_EMULATORS,
  FirebaseAnalyticsHandler,
} from '@awo-care/shared-firebase';
import { ANALYTICS_HANDLER } from '@solidgroup-agency/ui-shared-components';
import { LanguageService } from '@awo-care/shared-core';

const DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // Flaggen im Telefonfeld aus dem eigenen Build (project.json: flags/4x3),
    // relativ zum <base href> – kein Request an cdn.jsdelivr.net (DSGVO, #1040).
    { provide: PHONE_INPUT_FLAG_BASE_URL, useValue: 'flags/4x3' },
    // Custom handler as the main ErrorHandler
    {
      provide: ErrorHandler,
      useClass: CustomerErrorHandlerService,
    },
    // Add the official global listener
    // This connects the browser's global errors to the ErrorHandler
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(() => {
      const userService = inject(UserService);
      return userService.loadClaims();
    }),
    LanguageService, // Language routing for customer-ui

    provideHttpClient(withXhr()),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),

    // Browser-Titel je Route statt ueberall "AWO Care" (#1012).
    { provide: TitleStrategy, useExisting: PageTitleStrategy },
    { provide: DateAdapter, useClass: CustomGermanDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    importProvidersFrom(RecaptchaV3Module),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    { provide: GOOGLE_MAPS_API_KEY, useValue: environment.googleMapsAPIKey },

    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),

    // Firebase configuration
    ...provideFirebase(
      {
        ...environment.firebaseConfig,
        appId: environment.appIds.customerUi,
        measurementId: environment.measurementIds.customerUi,
      },
      {
        useEmulators: environment.useEmulators,
        useFunctionsEmulatorOnly: environment.useFunctionsEmulatorOnly,
      },
      {
        enableAnalytics:
          environment.production && !!environment.measurementIds.customerUi,
        enableAppCheck: environment.enableAppCheck,
        enablePerformance: environment.enablePerformance,
        performanceRequiresConsent: true,
        recaptchaSiteKey: environment.recaptchaSiteKey,
        tier: environment.tier,
        isTokenAutoRefreshEnabled: true,
      },
    ),
    // Provide emulator flag for services to inject
    { provide: USE_EMULATORS, useValue: environment.useEmulators },
    { provide: ANALYTICS_HANDLER, useClass: FirebaseAnalyticsHandler },
    provideCrossDomainCookieConsent(),
  ],
};
